import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sun, Moon, Globe, Check, Menu } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { TeacherSidebar } from "./TeacherSidebar";
import { MobileBottomNav, MobileDrawer } from "./MobileNav";
import NotificationBell from "./NotificationBell";
import TopBarSearch from "./TopBarSearch";
import { SUPPORTED_LANGUAGES } from "../i18n";
import "./tokens.css";
import greetingName from "../utils/greetingName";
import { BrandIcon, useBrand } from "./BrandMark";
import { useTheme } from "./theme";

/**
 * AppShell — the shared chrome for every v2 page.
 * Owns: theme (light/dark, persisted — ui/theme.js), topbar, optional preview ribbon.
 * Pages render their own <main> inside the children slot.
 */

function resolveDisplayName(user) {
  const fromProfile = user?.user?.name || user?.name;
  if (fromProfile) return fromProfile;
  try {
    const ls = JSON.parse(localStorage.getItem("user") || "null");
    return ls?.name || "Student";
  } catch {
    return "Student";
  }
}

function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="ux-ring h-10 px-2.5 rounded-xl ux-text-soft hover:ux-text hover:ux-surface-2 flex items-center gap-1.5 transition"
        title={t("topbar.language")}
      >
        <Globe className="w-4 h-4" />
        {/* The globe alone on a phone: the code made the top bar wider than 390 px (docs/32 B6). */}
        <span className="hidden sm:inline text-[12px] font-semibold uppercase tracking-wider">{current.code}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-44 ux-surface border ux-border rounded-xl ux-shadow-sm z-50 overflow-hidden">
          {SUPPORTED_LANGUAGES.map((l) => {
            const active = l.code === current.code;
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => change(l.code)}
                className="w-full px-3 h-9 flex items-center justify-between text-[13px] ux-text hover:ux-surface-2 transition"
              >
                <span className="flex items-center gap-2">
                  <span className="ux-text">{l.native}</span>
                  <span className="text-[10.5px] uppercase ux-text-muted">{l.code}</span>
                </span>
                {active && <Check className="w-3.5 h-3.5 text-indigo-500" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TopBar({ user, theme, onToggleTheme, role, onOpenMenu }) {
  const brand = useBrand();
  const { t } = useTranslation();
  const name = resolveDisplayName(user);
  return (
    <div className="sticky top-0 z-30 backdrop-blur-xl bg-[color-mix(in_oklab,var(--ux-surface),transparent_20%)] border-b ux-border">
      <div className="max-w-[var(--ux-content-box)] mx-auto pl-4 pr-4 md:pl-6 md:pr-6 py-3 flex items-center gap-3 md:gap-4">
        {/* Mobile: hamburger + brand (desktop rail is hidden below md) */}
        <button
          type="button"
          onClick={onOpenMenu}
          className="md:hidden ux-ring h-10 w-10 rounded-xl ux-text-soft hover:ux-text hover:ux-surface-2 flex items-center justify-center transition flex-none"
          aria-label={t("sidebar.open_menu")}
        >
          <Menu className="w-5 h-5" />
        </button>
        {/* The brand gives way on a narrow phone (its title truncates) so the bar never outgrows the screen (docs/32 B6). */}
        <div className="md:hidden flex items-center gap-2 min-w-0 flex-1">
          <BrandIcon className="w-8 h-8 flex-none" />
          <span className="min-w-0 text-[15px] font-semibold ux-text tracking-tight truncate">{brand.title}</span>
        </div>

        {/* Greeting hint replaces the brand in topbar — brand is now in sidebar */}
        <div className="hidden lg:block">
          <div className="text-[12px] ux-text-muted">{t("topbar.welcome_back")}</div>
          <div className="text-[14px] font-semibold ux-text tracking-tight -mt-0.5">{greetingName(name)}</div>
        </div>

        {/* A candidate's own exams (TopBarSearch); a teacher has no search to offer here. */}
        {role === "teacher" ? <div className="hidden md:block flex-1" /> : <TopBarSearch />}

        <div className="ml-auto flex flex-none items-center gap-1">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={onToggleTheme}
            className="ux-ring h-10 w-10 rounded-xl ux-text-soft hover:ux-text hover:ux-surface-2 flex items-center justify-center transition"
            title={t("topbar.toggle_theme")}
            aria-label={t("topbar.toggle_theme")}
            aria-pressed={theme === "dark"}
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <NotificationBell role={role} />
        </div>
      </div>
    </div>
  );
}

function handleSignOut() {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  } catch {}
  window.location.href = "/login";
}

export function AppShell({ user, children, role = "student" }) {
  const [theme, , toggleTheme] = useTheme();
  const name = resolveDisplayName(user);
  const SidebarComp = role === "teacher" ? TeacherSidebar : Sidebar;
  const [drawerOpen, setDrawerOpen] = useState(false);

  // When the sidebar is pinned, dock it (push content) instead of overlaying.
  const pinKey = role === "teacher" ? "ux-teacher-sidebar-pinned" : "ux-sidebar-pinned";
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem(pinKey) === "1"; } catch { return false; }
  });
  useEffect(() => {
    const onPin = (e) => setPinned(!!e.detail);
    const onStorage = (e) => { if (e.key === pinKey) setPinned(e.newValue === "1"); };
    window.addEventListener("ux-sidebar-pin", onPin);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("ux-sidebar-pin", onPin);
      window.removeEventListener("storage", onStorage);
    };
  }, [pinKey]);

  return (
    <div className="ux-root min-h-screen relative" data-theme={theme}>
      {/* Ambient mesh gradient — sits under everything for that React-y vibe */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -left-32 w-[40rem] h-[40rem] rounded-full bg-violet-500/15 blur-[120px]" />
        <div className="absolute top-1/3 -right-32 w-[36rem] h-[36rem] rounded-full bg-fuchsia-500/12 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 w-[34rem] h-[34rem] rounded-full bg-sky-500/12 blur-[120px]" />
      </div>

      {/* Desktop rail — hidden on mobile (replaced by bottom nav + drawer) */}
      <div className="hidden md:block">
        <SidebarComp userName={name} role={role === "teacher" ? "Teacher" : "Student"} onSignOut={handleSignOut} />
      </div>

      {/* Mobile navigation */}
      <MobileBottomNav role={role} onOpenMenu={() => setDrawerOpen(true)} />
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        role={role}
        userName={name}
        onSignOut={handleSignOut}
      />

      <div className={`md:pl-[64px] transition-[padding] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${pinned ? "md:!pl-[216px]" : ""}`}>
        <TopBar user={user} theme={theme} onToggleTheme={toggleTheme} role={role} onOpenMenu={() => setDrawerOpen(true)} />
        <main className="max-w-[var(--ux-content-box)] mx-auto px-5 md:px-6 py-4 md:py-6 space-y-5 pb-24 md:pb-6">
          {children}
        </main>
      </div>
    </div>
  );
}

export { resolveDisplayName };
